/* LiturgicalSeasonStrip — the archival ribbon that sits directly
 * beneath the altar-frontal sanctuary header on the Dashboard.
 *
 * A parish planning archive should always open with three anchors:
 *   • current liturgical season + colour (violet, green, white/gold, red, rose)
 *   • upcoming principal feast (name, date, days-out)
 *   • parish/dossier identity — supplied by the parent surface
 *
 * The colour swatch uses the liturgical-year tokens on brand.css. The
 * whole strip is rendered on parchment with a cast-bronze top rule and
 * a violet contemplative wash so it reads as one visual band with the
 * altar-frontal above it.
 *
 * Data is fetched from /api/liturgical/moment (deterministic, no LLM).
 * If the endpoint is unreachable the component silently renders null —
 * the dashboard must always load. */

import React, { useEffect, useState } from "react";
import axios from "axios";
import "./LiturgicalSeasonStrip.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map the backend colour label to the brand token used to render the
// swatch. Falls back to liturgical green if the label is unknown so we
// never render a raw hex from the client.
const SWATCH_TOKEN = {
  "Violet":           "var(--li-advent-violet)",
  "Green":            "var(--li-liturgical-green)",
  "Red":              "var(--li-liturgical-red)",
  "White & Gold":     "var(--li-liturgical-white)",
  "White":            "var(--li-liturgical-white)",
  "Rose":             "var(--li-liturgical-rose)",
  "Black":            "var(--li-liturgical-black)",
};

function swatchFor(colour) {
  return SWATCH_TOKEN[colour] || "var(--li-liturgical-green)";
}

function formatFeastDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function daysOutLabel(days) {
  const n = parseInt(days, 10);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  return `in ${n} days`;
}

export default function LiturgicalSeasonStrip() {
  const [moment, setMoment] = useState(null);

  useEffect(() => {
    let live = true;
    axios
      .get(`${API}/liturgical/moment`)
      .then((r) => { if (live) setMoment(r.data); })
      .catch(() => { /* silent — dashboard must always load */ });
    return () => { live = false; };
  }, []);

  if (!moment) return null;
  const { season, colour, next_feast: nextFeast } = moment;

  return (
    <div className="li-season-strip" data-testid="dash-season-strip">
      <div className="li-season-strip__inner">
        <div className="li-season-strip__block" data-testid="dash-season-current">
          <span className="li-season-strip__eyebrow">Current season</span>
          <div className="li-season-strip__value">
            <span
              className="li-season-strip__swatch"
              style={{ backgroundColor: swatchFor(colour) }}
              data-testid="dash-season-swatch"
              aria-label={`Vestment colour · ${colour}`}
            />
            <span className="li-season-strip__title">{season}</span>
            <span className="li-season-strip__colour">{colour}</span>
          </div>
        </div>

        <div className="li-season-strip__rule" aria-hidden="true" />

        {nextFeast && (
          <div className="li-season-strip__block" data-testid="dash-season-next-feast">
            <span className="li-season-strip__eyebrow">Next principal feast</span>
            <div className="li-season-strip__value">
              <span
                className="li-season-strip__swatch"
                style={{ backgroundColor: swatchFor(nextFeast.colour) }}
                aria-label={`Vestment colour · ${nextFeast.colour}`}
              />
              <span className="li-season-strip__title">{nextFeast.name}</span>
              <span className="li-season-strip__colour">
                {formatFeastDate(nextFeast.date)} · {daysOutLabel(nextFeast.days_out)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
